import { application } from './application'
import { Controller } from '@hotwired/stimulus'

import { Alert, ColorPreview, Tabs, Modal, Toggle } from 'tailwindcss-stimulus-components'
import { ClearableCheckboxController, ClearableInputController, ClearableResetButtonController, ClearableStatesController, ClearableSelectController } from './clearable'
import { DashboardController, WidgetController, WidgetFormController, WidgetSimpleFormController, WidgetPieController, WidgetBarController, WidgetLineController, WidgetCalendarController } from './dashboards'
import { FacilitiesFormController } from './facilities'
import { FieldsFormController, FieldsOptionController, FieldsNumericController, FieldsTableController } from './fields'
import { FormBuilderController, FormItemController, FormItemFormController, FormNewItemController, FormSectionController, FormResetInvalidController } from './forms'
import { GeniusHiddenInputsController, GeniusPopoverController, GeniusRefreshListController, GeniusSpinnerController } from './genius'
import GroupableGlobalToggleController from './groupable/global_toggle_controller'
import { LoginEnableAcknowledgeController, LoginSetCheckedController, TimeoutableSessionController } from './login'
import { Multiselect, BusinessProcessSortable, Sortable, SortableLocal, PhoneNumberInputController, DatepickerController, PopoverController, DropdownController, SimpleTabs, DynamicPlaceholder, DisableableFieldsetsController } from './ui'
import { NestedFormController, NestedConditionController, TemplatableController } from './nested'
import { PageViewController, CurrentPageViewController, PageViewSelectedColumnController, PageViewParamsCompressController } from './page_views'
import { ProjectTransitionFormController, ProjectBulkCopyController, ProjectCreationMessageController, UnsavedChangesController } from './projects'
import { RecordCounterOriginController, RecordCounterController } from './counters'
import { RefreshProgressBarController } from './bulk_actions'
import { RolesFormController, RolesSelectAllController, BulkRolesPermissionsController } from './roles'
import { SearchController, AdvancedSearchController } from './search'
import { TestIntegrationController, IntegrationsSpinnerController, RequestFormController } from './integrations'
import { WorkflowsFormController, WorkflowsEventController, WorkflowsActionController, WorkflowsActionRecipientController, WorkflowsSimpleModeController, WorkflowsActionGroupController } from './workflows'
import { RisksRiskAssessmentTableController } from './risks'
import RiskFormController from './risk_form_controller'
// @ts-expect-error
import Autosave from 'stimulus-rails-autosave'

import AdvancedFilterController from './advanced_filter_controller'
import AssessmentUrlController from './assessment_url_controller'
import UrlInputController from './url_input_controller'
import AppearanceController from './appearance_controller'
import AddEvidenceSlideoverController from './add_evidence_slideover_controller'
import AddEvidenceFormController from './add_evidence_form_controller'
import AuditorsSelect from './auditors_select_controller'
import BlurableController from './blurable_controller'
import BulkActionController from './bulk_action_controller'
import BulkActionsMenuController from './bulk_actions_menu_controller'
import BulkArchivedActionsController from './bulk_archived_actions_controller'
import BulkItemController from './bulk_item_controller'
import BulkSelectController from './bulk_select_controller'
import ButtonToggleController from './button_toggle_controller'
import CascadingSelectController from './cascading_select_controller'
import CancelChangesController from './cancel_changes_controller'
import ClearFiltersController from './clear_filters_controller'
import CheckboxesTreeController from './checkboxes_tree_controller'
import TreeFilterGroupController from './tree_filter_group_controller'
import ClickableRowController from './clickable_row_controller'
import ClipboardController from './clipboard_controller'
import CloneableAlertController from './cloneable_alert_controller'
import CollapsableController from './collapsable_controller'
import CollapseSidebarController from './collapse_sidebar_controller'
import ColorsSelectController from './colors_select_controller'
import ComplianceMetricsChartController from './compliance_metrics_chart_controller'
import CompaniesSelectController from './selectable/companies_select_controller'
import ControlResponseConfigurationRowController from './control_response/configuration_row_controller'
import ControlResponsesSelectController from './control_responses/select_controller'
import CopyTextController from './copy_text_controller'
import CopyValueController from './copy_value_controller'
import CountCheckboxesController from './count_checkboxes_controller'
import DialogAlertController from './dialog_alert_controller'
import DirectUploadController from './direct_upload_controller'
import FacilitiesFilterController from './selectable/facilities_filter_controller'
import FacilitiesSelectController from './selectable/facilities_select_controller'
import FieldDatabaseOptionsSelectController from './selectable/field_database_options_select_controller'
import FileRemovableController from './file_removable_controller'
import FilterCascadeController from './filter_cascade_controller'
import FilterController from './filter_controller'
import FlashMessageController from './flash_message_controller'
import FocusableController from './focusable_controller'
import FormResetController from './form_reset_controller'
import FormValidationController from './form_validation_controller'
import HiddenInputController from './hidden_input_controller'
import IconsSelectController from './icons_select_controller'
import ImagePreviewController from './image_preview_controller'
import ImageRemovalController from './image_removal_controller'
import ImportController from './import_controller'
import ThresholdsController from './thresholds_controller'
import LinkedSearchFormsController from './linked_search_forms_controller'
import LinkToBulkSelectController from './link_to_bulk_select_controller'
import LinkToDropdownController from './link_to_dropdown_controller'
import LinkToModalController from './link_to_modal_controller'
import LinkToSlideoverController from './link_to_slideover_controller'
import ListToggleController from './list_toggle_controller'
import AuditorUsersSelectController from './selectable/auditor_users_select_controller'
import ModalsContainerController from './modals_container_controller'
import MultiDirectUploadController from './multi_direct_upload_controller'
import MultiStepsFormController from './multi_steps_form_controller'
import NavigatableController from './navigatable_controller'
import NestableController from './nestable_controller'
import NotificationTemplatesFormController from './notification_templates/form_controller'
import OrganizationFacilitiesSelectController from './selectable/organization_facilities_select_controller'
import PasswordInputController from './password_input_controller'
import ParseDatesController from './parse_dates_controller'
import PerPageController from './per_page_controller'
import QueryFormController from './query_form_controller'
import RemovableController from './removable_controller'
import ResetSearchController from './reset_search_controller'
import RolePermissionsController from './role_permissions_controller'
import RoleStatusesController from './role_statuses_controller'
import SearchOptionsController from './search_options_controller'
import SelectableDropdownController from './selectable_dropdown_controller'
import SetActiveController from './set_active_controller'
import SettingsToggleableController from './settings_toggleable_controller'
import ShowMoreController from './show_more_controller'
import StatusesTransitionsController from './statuses_transitions_controller'
import SubmitController from './submit_controller'
import DeactivationSaveGuardController from './deactivation_save_guard_controller'
import SubmitEnableController from './submit_enable_controller'
import SubmitOnEnterController from './submit_on_enter_controller'
import ToggleableController from './toggleable_controller'
import ToggleAllController from './toggle_all_controller'
import ToggleDisableController from './toggle_disable_controller'
import ToggleListController from './toggle_list_controller'
import ToggleOutletController from './toggle_outlet_controller'
import TransitionsController from './transitions_controller'
import TurboFormController from './turbo_form_controller'
import TurboProgressController from './turbo_progress_controller'
import UserFilterController from './user_filter_controller'
import UsersMultiselectController from './users_multiselect_controller'
import VersionFormController from './version_form_controller'
import VisibilityTargetController from './visibility_target_controller'
import EvidencesFormController from './evidences/form_controller'
import EvidencesSearchController from './evidences/search_controller'
import EvidenceStatusChangeController from './evidence_status_change_controller'
import EvidenceStatusModalController from './evidence_status_modal_controller'
import VisibilityToggleController from './visibility_toggle_controller'
import Visible from './visible_controller'
import ScrollIntoViewController from './scroll_into_view_controller'
import ScrollPreserveController from './scroll_preserve_controller'
import HeatmapCellController from './risks/heatmap_cell_controller'
import HeatmapFilterController from './risks/heatmap_filter_controller'
import RiskCategoryFilterController from './risks/category_filter_controller'
import RiskFilterController from './risk_filter_controller'
import UserInputFilterController from './user_input_filter_controller'
import RiskFilterCountController from './risk_filter_count_controller'
import TreatmentStrategyController from './risks/treatment_strategies_controller'
import TypesAndCategories from './risks/types_and_categories_controller'
import SourceCauseNestedFormController from './source_cause_nested_form_controller'
import SourceCauseDropdownController from './source_cause_dropdown_controller'
import UsersSearchController from './risks/users_search_controller'
import InapplicabilityFormController from './inapplicability_form_controller'
import SlideoverController from './slideover_controller'
import SidebarMenuController from './sidebar/menu_controller'
import LinkControlsController from './risks/link_controls_controller'
import ControlsTreatmentsController from './risks/controls_treatments_controller'
import RiskTemplateFilterController from './risk_template_filter_controller'
import RiskTemplateNestedFormDeleteController from './risk_template_nested_form_delete_controller'
import RiskReportSpinnerController from './risks/reports/spinner_controller'
import RunScanFormController from './run_scan_form_controller'
import SpinnerController from './applications/spinner_controller'
import CategoryToggleController from './applications/category_toggle_controller'
import LibraryDownloadController from './risks/library/import/download_controller'
import ModeToggleController from './mode_toggle_controller'
import CopyToClipboardController from './copy_to_clipboard_controller'
import ExpandCodeController from './expand_code_controller'
import ShowMoreLessController from './show_more_less_controller'
import ColorTokenController from './color_token_controller'
import BusinessProcessToggleController from './business_process_toggle_controller'
import FrequencyController from './scheduled_workflows/frequency_controller'
import LinkedSelectController from './linked_select_controller'
import CategorySearchController from './category_search_controller'
import SubcategorySortableController from './ui/subcategory_sortable'
import FrameworkControlsController from './links/internal_controls/controls_treatments_controller'
import UiSearchController from './ui_search_controller'
import TechnologyEvaluationRowController from './technology_evaluation_row_controller'
import FrameworksDomainsControlsSelectorController from './links/internal_controls/frameworks_domains_controls_selector_controller'
import FrameworkControlSignatureController from './framework_control_signature_controller'
import FrameworkControlResponseMappingController from './control_responses/framework_control_response_mapping_controller'
import ControlResponseConfigurationFormController from './control_response_configuration_form_controller'
import ClearNewlyAddedIndicatorController from './clear_newly_added_indicator_controller'
import ViewportToggleController from './viewport_toggle_controller'
import ReportSpinnerController from './reports/spinner_controller'
import ToggleEvidenceResultsController from './toggle_evidence_results_controller'
import ToggleSectionsController from './toggle_sections_controller'
import ToggleAllCheckboxesController from './toggle_all_checkboxes_controller'
import ToggleRadioSectionController from './toggle_radio_section_controller'
import ScoringModeConfirmationController from './scoring_mode_confirmation_controller'
import FairCalculationTypeController from './fair/calculation_type_controller'
import FairExposureDistributionController from './fair/exposure_distribution_controller'
import LinkRiskAssetsController from './risks/assets/link_assets_controller'
import FilterRiskAssetsController from './risks/assets/filter_assets_controller'
import AddRiskFormController from './risks/add_risk_form_controller'
import BackdateJustificationController from './justification/backdate_controller'
import CollapsibleSectionController from './interface/sections/collapsible_section_controller'

application.register('add-risk-form', AddRiskFormController)
application.register('backdate-justification', BackdateJustificationController)
application.register('interface--sections--collapsible-section', CollapsibleSectionController)
application.register('advanced-filter', AdvancedFilterController)
application.register('assessment-url', AssessmentUrlController)
application.register('advanced-search', AdvancedSearchController)
application.register('add-evidence-slideover', AddEvidenceSlideoverController)
application.register('add-evidence-form', AddEvidenceFormController)
application.register('auditors-select', AuditorsSelect)
application.register('alert', Alert)
application.register('appearance', AppearanceController)
application.register('scoring-mode-confirmation', ScoringModeConfirmationController)
application.register('autosave', Autosave)
application.register('blurable', BlurableController)
application.register('bulk-action', BulkActionController)
application.register('bulk-actions-menu', BulkActionsMenuController)
application.register('bulk-archived-actions', BulkArchivedActionsController)
application.register('bulk-item', BulkItemController)
application.register('bulk-roles-permissions', BulkRolesPermissionsController)
application.register('bulk-select', BulkSelectController)
application.register('button-toggle', ButtonToggleController)
application.register('cascading-select', CascadingSelectController)
application.register('clear-filters', ClearFiltersController)
application.register('checkboxes-tree', CheckboxesTreeController)
application.register('tree-filter-group', TreeFilterGroupController)
application.register('clearable-checkbox', ClearableCheckboxController)
application.register('clearable-input', ClearableInputController)
application.register('clearable-reset-button', ClearableResetButtonController)
application.register('clearable-select', ClearableSelectController)
application.register('clearable-states', ClearableStatesController)
application.register('clickable-row', ClickableRowController)
application.register('clipboard', ClipboardController)
application.register('cloneable-alert', CloneableAlertController)
application.register('collapsable', CollapsableController)
application.register('collapse-sidebar', CollapseSidebarController)
application.register('color-preview', ColorPreview)
application.register('colors-select', ColorsSelectController)
application.register('compliance-metrics-chart', ComplianceMetricsChartController)
application.register('companies-select', CompaniesSelectController)
application.register('control-response-configuration-row', ControlResponseConfigurationRowController)
application.register('control-responses-select', ControlResponsesSelectController)
application.register('copy-text', CopyTextController)
application.register('copy-value', CopyValueController)
application.register('count-checkboxes', CountCheckboxesController)
application.register('current-page-view', CurrentPageViewController)
application.register('dashboard', DashboardController)
application.register('datepicker', DatepickerController)
application.register('dialog-alert', DialogAlertController)
application.register('direct-upload', DirectUploadController)
application.register('disableable-fieldsets', DisableableFieldsetsController)
application.register('dropdown', DropdownController as unknown as typeof Controller)
application.register('external-tabs', Tabs)
application.register('request-form', RequestFormController)
application.register('facilities-filter', FacilitiesFilterController)
application.register('facilities-form', FacilitiesFormController)
application.register('facilities-select', FacilitiesSelectController)
application.register('field-database-options-select', FieldDatabaseOptionsSelectController)
application.register('fields-form', FieldsFormController)
application.register('fields-numeric', FieldsNumericController)
application.register('fields-option', FieldsOptionController)
application.register('fields-table', FieldsTableController)
application.register('file-removable', FileRemovableController)
application.register('filter-cascade', FilterCascadeController)
application.register('filter', FilterController)
application.register('flash-message', FlashMessageController)
application.register('focusable', FocusableController)
application.register('form-builder', FormBuilderController)
application.register('form-item-form', FormItemFormController)
application.register('form-item', FormItemController)
application.register('form-new-item', FormNewItemController)
application.register('form-reset-invalid', FormResetInvalidController)
application.register('url-input', UrlInputController)
application.register('form-reset', FormResetController)
application.register('form-validation', FormValidationController)
application.register('form-section', FormSectionController)
application.register('genius-hidden-inputs', GeniusHiddenInputsController)
application.register('genius-popover', GeniusPopoverController)
application.register('genius-refresh-list', GeniusRefreshListController)
application.register('genius-spinner', GeniusSpinnerController)
application.register('hidden-input', HiddenInputController)
application.register('scroll-into-view', ScrollIntoViewController)
application.register('scroll-preserve', ScrollPreserveController)
application.register('icons-select', IconsSelectController)
application.register('image-preview', ImagePreviewController)
application.register('image-removal', ImageRemovalController)
application.register('import', ImportController)
application.register('inapplicability-form', InapplicabilityFormController)
application.register('linked-search-forms', LinkedSearchFormsController)
application.register('link-to-bulk-select', LinkToBulkSelectController)
application.register('link-to-dropdown', LinkToDropdownController)
application.register('link-to-modal', LinkToModalController)
application.register('link-to-slideover', LinkToSlideoverController)
application.register('list-toggle', ListToggleController)
application.register('login-enable-acknowledge', LoginEnableAcknowledgeController)
application.register('login-set-checked', LoginSetCheckedController)
application.register('auditor-users-select', AuditorUsersSelectController)
application.register('modal', Modal as unknown as typeof Controller)
application.register('modals-container', ModalsContainerController)
application.register('multiselect', Multiselect)
application.register('multi-direct-upload', MultiDirectUploadController)
application.register('multi-steps-form', MultiStepsFormController)
application.register('navigatable', NavigatableController)
application.register('nestable', NestableController)
application.register('nested-condition', NestedConditionController)
application.register('nested-form', NestedFormController)
application.register('notification-templates-form', NotificationTemplatesFormController)
application.register('organization-facilities-select', OrganizationFacilitiesSelectController)
application.register('page-view-selected-column', PageViewSelectedColumnController)
application.register('page-view-params-compress', PageViewParamsCompressController)
application.register('page-view', PageViewController)
application.register('password-input', PasswordInputController)
application.register('per-page', PerPageController)
application.register('phone-number-input', PhoneNumberInputController)
application.register('popover', PopoverController)
application.register('dynamic-placeholder', DynamicPlaceholder)
application.register('project-bulk-copy', ProjectBulkCopyController)
application.register('project-creation-message', ProjectCreationMessageController)
application.register('project-transition-form', ProjectTransitionFormController)
application.register('parse-dates', ParseDatesController)
application.register('query-form', QueryFormController)
application.register('record-counter-origin', RecordCounterOriginController)
application.register('record-counter', RecordCounterController)
application.register('refresh-progress-bar', RefreshProgressBarController)
application.register('removable', RemovableController)
application.register('reset-search', ResetSearchController)
application.register('risks--heatmap-cell', HeatmapCellController)
application.register('risks--heatmap-filter', HeatmapFilterController)
application.register('risks-types-and-categories', TypesAndCategories)
application.register('role-permissions', RolePermissionsController)
application.register('role-statuses', RoleStatusesController)
application.register('roles-form', RolesFormController)
application.register('roles-select-all', RolesSelectAllController)
application.register('search-options', SearchOptionsController)
application.register('search', SearchController)
application.register('selectable-dropdown', SelectableDropdownController)
application.register('set-active', SetActiveController)
application.register('settings-toggleable', SettingsToggleableController)
application.register('show-more', ShowMoreController)
application.register('simple-tabs', SimpleTabs)
application.register('sortable-local', SortableLocal)
application.register('sortable', Sortable)
application.register('statuses-transitions', StatusesTransitionsController)
application.register('submit-on-enter', SubmitOnEnterController)
application.register('submit', SubmitController)
application.register('deactivation-save-guard', DeactivationSaveGuardController)
application.register('submit-enable', SubmitEnableController)
application.register('tabs', Tabs)
application.register('templatable', TemplatableController)
application.register('groupable--global-toggle', GroupableGlobalToggleController)
application.register('test-integration', TestIntegrationController)
application.register('integrations-spinner', IntegrationsSpinnerController)
application.register('timeoutable-session', TimeoutableSessionController)
application.register('toggle', Toggle)
application.register('toggle-all', ToggleAllController)
application.register('toggle-disable', ToggleDisableController)
application.register('toggle-list', ToggleListController as unknown as typeof Controller)
application.register('toggle-outlet', ToggleOutletController)
application.register('toggleable', ToggleableController)
application.register('transitions', TransitionsController)
application.register('turbo-form', TurboFormController)
application.register('turbo-progress', TurboProgressController)
application.register('unsaved-changes', UnsavedChangesController)
application.register('user-filter', UserFilterController)
application.register('users-multiselect', UsersMultiselectController)
application.register('version-form', VersionFormController)
application.register('visibility-target', VisibilityTargetController)
application.register('evidences-form', EvidencesFormController)
application.register('evidences-search', EvidencesSearchController)
application.register('evidence-status-change', EvidenceStatusChangeController)
application.register('evidence-status-modal', EvidenceStatusModalController)
application.register('visibility-toggle', VisibilityToggleController)
application.register('visible', Visible)
application.register('widget-bar', WidgetBarController)
application.register('widget-calendar', WidgetCalendarController)
application.register('widget-form', WidgetFormController)
application.register('widget-line', WidgetLineController)
application.register('widget-pie', WidgetPieController)
application.register('widget-simple-form', WidgetSimpleFormController)
application.register('widget', WidgetController)
application.register('workflows-action-group', WorkflowsActionGroupController)
application.register('workflows-action-recipient', WorkflowsActionRecipientController)
application.register('workflows-action', WorkflowsActionController)
application.register('workflows-event', WorkflowsEventController)
application.register('workflows-form', WorkflowsFormController)
application.register('workflows-simple-mode', WorkflowsSimpleModeController)
application.register('risk-assessment', RisksRiskAssessmentTableController)
application.register('cancel-changes', CancelChangesController)
application.register('risk-form', RiskFormController)
application.register('risk-category-filter', RiskCategoryFilterController)
application.register('risk-filter', RiskFilterController)
application.register('run-scan-form', RunScanFormController)
application.register('user-input-filter', UserInputFilterController)
application.register('risk-filter-count', RiskFilterCountController)
application.register('risks--treatment-strategy', TreatmentStrategyController)
application.register('source-cause-nested-form', SourceCauseNestedFormController)
application.register('source-cause-dropdown', SourceCauseDropdownController)
application.register('users-search', UsersSearchController)
application.register('thresholds', ThresholdsController)
application.register('slideover', SlideoverController as unknown as typeof Controller)
application.register('sidebar-menu', SidebarMenuController)
application.register('link-controls', LinkControlsController)
application.register('controls-treatments', ControlsTreatmentsController)
application.register('risk-template-filter', RiskTemplateFilterController)
application.register('risk-template-nested-form-delete', RiskTemplateNestedFormDeleteController)
application.register('risks--reports--spinner', RiskReportSpinnerController)
application.register('business-process-sortable', BusinessProcessSortable)
application.register('applications--spinner', SpinnerController)
application.register('applications--category-toggle', CategoryToggleController)
application.register('risks--library--import--download', LibraryDownloadController)
application.register('mode-toggle', ModeToggleController)
application.register('copy-to-clipboard', CopyToClipboardController)
application.register('expand-code', ExpandCodeController)
application.register('show-more-less', ShowMoreLessController)
application.register('color-token', ColorTokenController)
application.register('business-process-toggle', BusinessProcessToggleController)
application.register('scheduled-workflows--frequency', FrequencyController)
application.register('linked-select', LinkedSelectController)
application.register('category-search', CategorySearchController)
application.register('subcategory-sortable', SubcategorySortableController)
application.register('frameworks-controls-treatments', FrameworkControlsController)
application.register('ui-search', UiSearchController)
application.register('technology-evaluation-row', TechnologyEvaluationRowController)
application.register('frameworks-domains-controls-selector', FrameworksDomainsControlsSelectorController)
application.register('framework-control-signature', FrameworkControlSignatureController)
application.register('framework-control-response-mapping', FrameworkControlResponseMappingController)
application.register('control-response-configuration-form', ControlResponseConfigurationFormController)
application.register('clear-newly-added-indicator', ClearNewlyAddedIndicatorController)
application.register('viewport-toggle', ViewportToggleController)
application.register('reports--spinner', ReportSpinnerController)
application.register('toggle-evidence-results', ToggleEvidenceResultsController)
application.register('toggle-sections', ToggleSectionsController)
application.register('toggle-all-checkboxes', ToggleAllCheckboxesController)
application.register('toggle-radio-section', ToggleRadioSectionController)
application.register('fair-calculation-type', FairCalculationTypeController)
application.register('fair-exposure-distribution', FairExposureDistributionController)
application.register('link-risk-assets', LinkRiskAssetsController)
application.register('filter-risk-assets', FilterRiskAssetsController)
